// ====================================================================
// ziven-theme-aura v0.2.10 modifyDrawer.js
// ====================================================================
//
// 移动端 drawer 注入 (< 992px)
// 桌面端 (>= 992px) 走 modifySidebar.js, drawer 内部 ziven-theme-aura 注入的元素被 CSS 隐藏
//   但 vendor top bar (含 logo + 搜索 + 通知 + 头像) 在桌面端继续显示
//
// 顺序 (跟 ziven.ski 1.8 真实 1:1, 跟设计稿 v8 一致, 辉哥 07:42 拍板):
//   user-block (顶部) → 5 stats (紧跟 user-block) → nav section (8 nav) → vendor 4 扩展
//
// v0.2.10 (辉哥 17:44 拍板 A 方案): 未登录也注入 nav section, 1.8 ziven.ski 1:1 复刻
//   - 删 L24-25 `if (!user) return;` 限制
//   - user-block + 5 stats 包到 `if (user) { ... }` 内 (登录态才有)
//   - .App-drawer-aura-logged-in class 也只登录态加 (CSS 用来隐藏论坛名称, 未登录显示 logo)
//   - nav section 任何时候都跑, 1:1 复刻 ziven.ski 1.8 改造的未登录态
//     (vendor IndexSidebar.navItems() 默认给 All Discussions + Tags 2 nav,
//      vendor flarum-tags 也会加 tags nav, 跟 1.8 ziven-pallet-theme 一致)
//   - addAccountNavItems L249-251 已有 `if (!user) return;` 保护, 未登录不追加 3 账号 nav
//
// 登录态检测: drawer 加 .App-drawer-aura-logged-in class
//   CSS 用 .App-drawer-aura-logged-in .Header-title { display: none } 隐藏论坛名称
//
// 桌面端详见 modifySidebar.js
// ====================================================================

import app from 'flarum/common/app';
import { extend, override } from 'flarum/common/extend';
import Drawer from 'flarum/common/utils/Drawer';
import { buildUserBlock, buildStatsSection, buildNavSection, renderNavItems } from './buildElements';

// === 注入 user-block + 5 stats + nav section (mobile drawer 模式) ===
function injectMobileSidebar() {
  const user = app.session.user;
  const drawer = document.getElementById('drawer');
  if (!drawer) return;

  // 桌面端走 modifySidebar.js, 这里不注入避免重复
  if (window.matchMedia('(min-width: 992px)').matches) return;

  // === 加 loggedIn class (CSS 用来隐藏论坛名称, 仅登录态加) ===
  if (user) {
    drawer.classList.add('App-drawer-aura-logged-in');

    // === 1. user-block 注入: 顶部 (prepend) ===
    if (!drawer.querySelector('.App-drawer-aura-user-block')) {
      const userBlock = buildUserBlock(user);
      if (drawer.firstChild) {
        drawer.insertBefore(userBlock, drawer.firstChild);
      } else {
        drawer.appendChild(userBlock);
      }
    }

    // === 2. 5 stats 注入: 紧跟 user-block ===
    if (!drawer.querySelector('.App-drawer-aura-stats')) {
      // v0.2.6: 传 user, 改读真实数据
      const statsSection = buildStatsSection(user);
      const userBlock = drawer.querySelector('.App-drawer-aura-user-block');
      if (userBlock && userBlock.nextSibling) {
        drawer.insertBefore(statsSection, userBlock.nextSibling);
      } else if (userBlock) {
        drawer.appendChild(statsSection);
      } else {
        if (drawer.firstChild) {
          drawer.insertBefore(statsSection, drawer.firstChild);
        } else {
          drawer.appendChild(statsSection);
        }
      }
    }
  }

  // === 3. nav section 注入: 在 vendor header 之前 (登录 + 未登录都注入, v0.2.10) ===
  // 顺序 (登录态): 5 stats → nav (8 nav: 5 主体 + 1 separator + 3 账号)
  // 顺序 (未登录态, 1.8 ziven.ski 1:1): nav (2 nav: All Discussions + Tags, vendor flarum-tags 也会加 tags)
  // 注意: ziven.ski 真实顺序: 5 stats → separator → 8 nav, 但我们简化, 不画 separator 在 5 stats 后
  //       (跟 v0.1.0c 一致)
  if (!drawer.querySelector('.App-drawer-aura-nav')) {
    const navSection = buildNavSection();
    // 注入位置: 5 stats 之后 (登录态) 或 drawer 头部 (未登录态) → vendor header 之前
    const statsSection = drawer.querySelector('.App-drawer-aura-stats');
    if (statsSection && statsSection.nextSibling) {
      drawer.insertBefore(navSection, statsSection.nextSibling);
    } else {
      drawer.appendChild(navSection);
    }
  }

  // v0.2.5b: 删 m.redraw() (强制 sync mithril redraw, 跟 vendor 路由 redraw scheduler 死锁 / 重复, 可能触发 SPA 跳路由时 console 错误)
  // vendor mithril 自己调度 redraw, 不需要我们强制触发
}

// === v0.2.8: 路由切换时重新 render nav items (让 vendor LinkButton.isActive 重算) ===
// 移动端 drawer 实际显示, 路由切换时让 vendor LinkButton.isActive 重算
// mithril m.render() 模式: 一次性渲染, mithril m.route() 切换不重画, LinkButton.active 写死在 DOM
// 修法: 监听 mithril 路由变化 (popstate + monkey-patch pushState), 重新 m.render 触发 LinkButton 重新 initAttrs
// v0.2.8 修 (重要): mithril 0.2.x 路由切换是异步的, m.render 同步调用时 m.route.get() 仍返回旧值
//   - 修法: 在 pushState 调用时 (monkey-patch 内部) 同步缓存 currentPath 到 window
//   - refreshNavActive 读 window.__ziven_aura_current_path__ 传给 renderNavItems
//   - renderNavItems 给 vendor LinkButton 强制覆盖 active 属性
function refreshNavActive() {
  console.log('[ziven-theme-aura] refreshNavActive called, currentPath=', window.__ziven_aura_current_path__);
  setTimeout(() => {
    const drawer = document.getElementById('drawer');
    if (!drawer) return;
    const navSection = drawer.querySelector('.App-drawer-aura-nav');
    if (!navSection) return;
    try {
      // 重新 render nav items (让 vendor LinkButton.isActive 重算, 因为 mithril 路由变了)
      // v0.2.8 修: 传 currentPath (m.route.get() 在异步路由切换期间可能返回旧值)
      renderNavItems(navSection, window.__ziven_aura_current_path__);
    } catch (e) {
      console.error('[ziven-theme-aura] refreshNavActive (mobile) failed:', e);
    }
  }, 0);
}

// === 监听登录状态 + viewport 变化 ===
function setupObserver() {
  setTimeout(injectMobileSidebar, 100);

  // 监听 mithril redraw
  extend('flarum/common/Component', 'onupdate', function () {
    if (document.getElementById('drawer') && !document.querySelector('.App-drawer-aura-user-block')) {
      injectMobileSidebar();
    }
  });

  // 监听 viewport 切换 (resize, 移动端 ↔ 桌面端切换)
  window.matchMedia('(min-width: 992px)').addEventListener('change', () => {
    // viewport 切换时, 清空 ziven-theme-aura 注入的元素让另一边接管
    const drawer = document.getElementById('drawer');
    if (drawer) {
      drawer.querySelectorAll('.App-drawer-aura-user-block, .App-drawer-aura-stats, .App-drawer-aura-nav').forEach(el => el.remove());
    }
    const sidebar = document.querySelector('.App-sidebar-aura');
    if (sidebar) sidebar.remove();
    // 触发自定义事件, 让 modifySidebar 重新评估
    window.dispatchEvent(new CustomEvent('ziven-aura-viewport-changed'));
    // 自己也重新评估 (mobile 端重新注入)
    if (!window.matchMedia('(min-width: 992px)').matches) {
      setTimeout(injectMobileSidebar, 100);
    }
  });

  // v0.2.8 改回: 监听 mithril 路由变化, 重新 render nav items
  // m.render() 模式不会自动重画, 需要手动触发让 LinkButton 重新 initAttrs
  window.addEventListener('popstate', refreshNavActive);
  // v0.2.8 修 (重要, SOP 133 候选): chain pattern 修 monkey-patch
  //   - 之前用 `window.__ziven_aura_patched__` guard, 但被 Playwright 客户端先 patch 占用
  //   - ziven-theme-aura 的 listener 永远不被调用, 路由切换时 active 不更新
  //   - 修法: 改 guard name `__ziven_aura_patched_v3__` + chain pattern
  //     找到链头 `currentPush.__ziven_aura_original_v3__ || currentPush`,
  //     不会覆盖 Playwright 客户端的 patch, 链头永远指向原生 pushState
  //   - 跟 modifySidebar.js 共享同一个 guard, 避免双 patch
  //   - 同时在 pushState 调用的瞬间, 同步缓存 currentPath 到 window.__ziven_aura_current_path__
  //     (因为 mithril 0.2.x 路由切换是异步的, m.render 同步调用时 m.route.get() 仍返回旧值)
  if (!window.__ziven_aura_patched_v3__) {
    window.__ziven_aura_patched_v3__ = true;
    const currentPush = history.pushState;
    const originalPush = currentPush.__ziven_aura_original_v3__ || currentPush;
    const wrappedPush = function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      // args[2] = URL (pushState state, title, url)
      // 但 mithril 调 pushState 时 url 可能是 undefined (用当前 m.route.get())
      // 兜底用 window.location.pathname (pushState 调用后 location 还没变, 用 args[2] 或 m.route.get())
      originalPush.apply(this, args);
      // v0.2.8: 同步缓存 currentPath 给 refreshNavActive 用
      window.__ziven_aura_current_path__ = args[2] || m.route.get() || window.location.pathname;
      window.dispatchEvent(new CustomEvent('ziven-aura-route-changed'));
    };
    wrappedPush.__ziven_aura_original_v3__ = originalPush;
    history.pushState = wrappedPush;
    const currentReplace = history.replaceState;
    const originalReplace = currentReplace.__ziven_aura_original_v3__ || currentReplace;
    const wrappedReplace = function () {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }
      originalReplace.apply(this, args);
      window.__ziven_aura_current_path__ = args[2] || m.route.get() || window.location.pathname;
      window.dispatchEvent(new CustomEvent('ziven-aura-route-changed'));
    };
    wrappedReplace.__ziven_aura_original_v3__ = originalReplace;
    history.replaceState = wrappedReplace;

    // 初始化 currentPath (页面首次加载时)
    window.__ziven_aura_current_path__ = window.location.pathname;
  }
  window.addEventListener('ziven-aura-route-changed', refreshNavActive);
}

// ====================================================================
// 移动端 drawer 开关时保留页面滚动位置
// ====================================================================
//
// 症状: 移动端往下滚几屏 → 点开 drawer 看一眼 → 关闭 → 页面被重置到顶部.
//
// 根因: less/forum.less §16 段用 `body:has(.App.drawerOpen) { position: fixed }`
//   锁 body 滚动 (辉哥 2026-10-19 01:05 反馈: 抽屉里滑动时背景 forum 跟着滚).
//   body 一旦 position:fixed 就脱离文档流 → 文档高度塌缩 → scrollY 被 clamp 到 0;
//   抽屉关掉后没人把滚动位置还原回去 ⇒ 用户被弹回顶部.
//   那段 CSS 里预留了 `top: var(--ziven-aura-drawer-scroll-top, 0)`, 但**从来没有 JS
//   写过这个变量** (grep 全仓库: 变量只在 less 里出现过) ⇒ top 永远是 fallback 0,
//   连"冻结瞬间背景视觉不跳动"也没做到.
//
// 修法 (补上 CSS 注释里承诺的那段 JS):
//   show 时: body 还是 position:static ⇒ window.scrollY 还是真实位置 ⇒ 记下来写成
//            CSS 变量 `--ziven-aura-drawer-scroll-top: -900px` (挂在 documentElement,
//            body 继承) ⇒ body 变 fixed 后 top:-900px ⇒ 背景视觉位置不动, 不闪.
//   hide 时: core 已移除 drawerOpen class ⇒ body 恢复 static ⇒ 清掉 CSS 变量 +
//            window.scrollTo(0, 900) 把位置还回去.
//
// 为什么 show 必须用 override 而不是 extend:
//   extend 的语义 (vendor/flarum/core/js/src/common/extend.ts): 包装器**先**执行原函数、
//   **后**执行回调, 回调第一个参数是原函数的返回值. core 的 Drawer.show() 一执行就
//   classList.add('drawerOpen') ⇒ body 立刻 position:fixed ⇒ scrollY 当场被 clamp 成 0
//   ⇒ 等回调跑时已经读不到 900 了.
//   override 的语义: 包装器把 original.bind(this) 作为**第一个参数**传给你的函数,
//   你在调 original **之前**就能拿到 window.scrollY.
//
// 为什么 hide 用 extend:
//   extend 的回调天然在原函数**之后**跑 ⇒ core 的 Drawer.hide() (Drawer.js:101
//   classList.remove('drawerOpen')) 已经跑完 ⇒ body 恢复 position:static ⇒ 这时
//   scrollTo 才生效.
//
// 为什么 hide 时要区分"用户关闭"和"路由跳转关闭":
//   core 每次路由切换都会关抽屉 (vendor/flarum/core/js/src/common/components/Page.tsx:38
//   → oninit 里 app.drawer.hide()). 如果那时也还原滚动位置, 新页面就会停在上一个页面
//   的 900px 处 ⇒ 错.
//   判据: show 时记下 window.location.href, hide 时比对. URL 变了 = 路由跳转 ⇒ 只清 CSS
//   变量不还原滚动; URL 没变 = 用户自己关的 ⇒ 还原.
//
// 转屏 (移动端 → 桌面) 的情况:
//   core 的 resizeHandler (Drawer.js:56) 在视口拉宽时调 hide(). 此时 body 从
//   position:fixed 回到 static, 滚动位置同样会丢. 这时 URL 没变 ⇒ 照样还原.
//   ⇒ hide 时**不能**用媒体查询卡住 (只有 show 时判断移动端).
// ====================================================================

// less/forum.less §16 段读的变量名 (挂在 documentElement 上, body 继承)
const DRAWER_SCROLL_TOP_VAR = '--ziven-aura-drawer-scroll-top';

// 跟 less 里 `@media (max-width: 991.98px)` 保持一致
const MOBILE_MEDIA_QUERY = '(max-width: 991.98px)';

// 已记下的滚动位置 (null = 当前没有冻结中的抽屉)
let savedScrollY = null;
// 记下滚动位置时的 URL (用来区分"用户关闭" vs "路由跳转关闭")
let savedUrl = null;

// === show: 冻结前把 scrollY 记下来, 写成 CSS 变量 ===
function captureDrawerScroll() {
  // 抽屉已经开着又被再次 show (重复点导航按钮): 位置已经被 clamp 成 0,
  // 再记一次会把真实位置覆盖成 0 ⇒ 已有记录就不动
  if (savedScrollY !== null) return;

  // 只在移动端做: 桌面端 body 永远不会被冻结, 没有位置可丢
  if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
  savedScrollY = window.scrollY || window.pageYOffset || 0;
  savedUrl = window.location.href;

  // body 变 position:fixed 后 top:-900px ⇒ 冻结瞬间背景视觉位置不动 (不闪)
  document.documentElement.style.setProperty(DRAWER_SCROLL_TOP_VAR, "-".concat(savedScrollY, "px"));
}

// === hide: 清 CSS 变量 + 还原滚动位置 ===
function restoreDrawerScroll() {
  const y = savedScrollY;
  const url = savedUrl;
  savedScrollY = null;
  savedUrl = null;

  // 清变量 (不留脏值给下一次)
  document.documentElement.style.removeProperty(DRAWER_SCROLL_TOP_VAR);

  // 没有冻结记录 (抽屉本来就没开) ⇒ 什么都不用做
  if (y === null) return;

  // 路由跳转关闭 (core Page.oninit → app.drawer.hide()): 新页面该从顶部开始,
  // 不能把上一个页面的滚动位置带过去 ⇒ URL 变了就不还原.
  // 转屏到桌面时 URL 没变 ⇒ 照样还原 (body 从 fixed 回 static 位置也会丢).
  if (window.location.href !== url) return;

  // 用两参形式 scrollTo (老式 API, 兼容性最好).
  // 注意: 它**不保证**瞬时 —— 若站点将来给 html 设了 scroll-behavior: smooth,
  // 这里会变成动画滚动. 不用 scrollTo({top, behavior:'instant'}) 形式:
  // 在不认识 options 形式的老浏览器上, scrollTo(NaN, undefined) 按规范是 no-op
  // ⇒ 滚动位置根本还原不了, 比动画更糟.
  window.scrollTo(0, y);
}
export function setupDrawerScrollLock() {
  // 幂等守卫 (initializer 万一跑两次别叠补丁)
  if (window.__ziven_aura_drawer_scroll_lock__) return;
  window.__ziven_aura_drawer_scroll_lock__ = true;

  // show: 必须 override (要在 core 加 drawerOpen class **之前**读 scrollY)
  override(Drawer.prototype, 'show', function (original) {
    // 绝不能让 captureDrawerScroll 的异常抛出去: Flarum override 的包装器是
    //   try { return newMethod.apply(...) } catch (e) { app.handleErrorOnce(...) }
    // ⇒ newMethod 在调 original **之前**抛错 ⇒ original 根本不执行 ⇒ 抽屉完全打不开.
    // 这里失败只降级 (本次不保存滚动位置 = 退回修复前的行为, 不算更坏), 不能炸掉.
    try {
      captureDrawerScroll();
    } catch (e) {
      console.warn('[ziven-theme-aura] captureDrawerScroll failed, 抽屉滚动位置不保存', e);
    }
    // override 的返回值会透传 ⇒ 必须 return
    // arguments[0] 是 original (override 塞进来的), 真正的原方法参数从 [1] 开始
    return original.apply(this, Array.prototype.slice.call(arguments, 1));
  });

  // hide: extend 的回调天然在 core 移除 drawerOpen class **之后**跑
  extend(Drawer.prototype, 'hide', function () {
    restoreDrawerScroll();
  });
}

// ====================================================================
// 移动端抽屉「点两下才收起」: 用 touchend 主动关闭, 绕开 iOS 不合成 click
// ====================================================================
//
// 症状: iPhone / iOS Safari 上打开移动端抽屉后, 点抽屉右侧的灰色空白区 (backdrop),
//       要点**两下**抽屉才收起; 桌面端 / Chromium 一次就关.
//
// 已排除的假设 (都做过实测, 别再重复):
//   1. ziven-core 的 touchmove preventDefault —— 生产已删, backdrop 事件表只剩 {"click":1}
//   2. Flarum focus-trap —— createFocusTrap('#drawer', {allowOutsideClick:true,
//      clickOutsideDeactivates:true}) 这组选项下 checkPointerDown / checkClick
//      两个分支都短路, 不会 preventDefault
//   3. backdrop 被遮挡 / 落点错 —— elementFromPoint(300/330/360/385, 500)
//      命中的就是 DIV.drawer-backdrop 本身 (z-index 1040, pointer-events auto)
//   4. 软键盘吞点击 —— 抽屉打开后 document.activeElement 是 <a> (64x64), 不是 input
//   5. iOS "priming tap" (第一下只给 :hover 吞掉 click) —— 生产的 forum.css 全量解析:
//      backdrop 及其祖先 (html / body) 一条 :hover 规则都没有
//   6. body{position:fixed} (锁 body 滚动那段的 CSS) —— 滚到 400px 再开抽屉,
//      命中测试 y=120/300/500/700 全部命中 backdrop, 一下就关、scrollY 正确还原 400
//   7. 页面/结构问题 —— 生产抽屉 dump 一切正常 (drawer 270x844 fixed z1050)
//
// 结论: iOS Safari 在这个场景**不合成 click** (具体内部机制**尚未在真机定位到**),
//       而 core 关闭抽屉的通路只有一条:
//       vendor/flarum/core/js/src/common/utils/Drawer.js:114
//       $('<div/>').addClass('drawer-backdrop fade').appendTo('body').on('click', this.hide.bind(this))
//       click 不来 ⇒ 抽屉永远关不掉.
//
// 修法: 触摸序列里 touchstart / touchend 必定触发, 而且**早于**浏览器决定要不要
//       合成 click ⇒ 改用 touchend 主动关闭, 绕开 iOS 的 click 合成判定;
//       同时 preventDefault 掐掉随后合成的 click, 避免幽灵点击打到抽屉后面的内容.
//
//   ⚠️ 如实说明: 这是**绕开 click 合成的稳妥做法**, 不是已定位根因 —— iOS 内部到底
//      为什么在这个场景不合成 click, 仍未定位; 本修复不依赖那个原因.
// ====================================================================

// 滑动判定阈值 (px): 起点到终点的直线距离超过它 ⇒ 认为是滑动不是点击
const TOUCH_TAP_TOLERANCE = 10;
export function setupTouchDismiss() {
  // 幂等守卫 (initializer 万一跑两次别叠监听)
  if (window.__ziven_aura_touch_dismiss__) return;
  window.__ziven_aura_touch_dismiss__ = true;

  // touchstart 起点坐标 (只读、不 preventDefault ⇒ 不影响滚动性能)
  let startX = null;
  let startY = null;
  document.addEventListener('touchstart', e => {
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    startX = t.clientX;
    startY = t.clientY;
  }, {
    capture: true,
    passive: true
  });

  // touchend (passive:false —— 只有这样才能 preventDefault)
  document.addEventListener('touchend', e => {
    // 每个 touchend 消费掉自己的起点 (避免用上一轮的坐标判滑动)
    const sx = startX;
    const sy = startY;
    startX = null;
    startY = null;
    try {
      // 抽屉没开 ⇒ 不干预.
      // ⚠️ 这一条**必须排在 preventDefault 之前**, 否则会吞掉抽屉关闭状态下
      //    的正常点击 (比如点页面上的任何东西).
      if (!app.drawer || !app.drawer.isOpen()) return;
      const el = e.target;
      if (!el || typeof el.closest !== 'function') return;

      // 抽屉内部 (点 nav 链接等) 交给 core 处理 —— 我们不 preventDefault,
      // 让 core 合成出来的 click 照常触发跳转
      if (el.closest('#drawer')) return;

      // modal 保护 (两条都要):
      //   - core 明确允许"从抽屉里的 nav 组件打开 modal", modal 叠在抽屉上时
      //     不能把抽屉关掉
      if (el.closest('.ModalManager')) return;
      if (app.modal && typeof app.modal.isModalOpen === 'function' && app.modal.isModalOpen()) return;

      // 滑动判定: 起点到终点 > 10px ⇒ 是滑动不是点击 ⇒ 不关
      const t = e.changedTouches && e.changedTouches[0];
      if (sx !== null && sy !== null && t) {
        const dx = t.clientX - sx;
        const dy = t.clientY - sy;
        if (Math.sqrt(dx * dx + dy * dy) > TOUCH_TAP_TOLERANCE) return;
      }

      // 掐掉随后合成的 click (避免幽灵点击打到抽屉后面的内容)
      if (e.cancelable) e.preventDefault();
      app.drawer.hide();
    } catch (err) {
      // 绝不能让异常冒出去影响页面其它交互
      console.warn('[ziven-theme-aura] touchend dismiss failed', err);
    }
  }, {
    capture: true,
    passive: false
  });
}
export default function modifyDrawer() {
  setupObserver();
  setupDrawerScrollLock();
  setupTouchDismiss();
}